import React, { useMemo } from 'react';
import {
  Badge,
  Card,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { Info } from 'lucide-react';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { useHeartRateStore } from '@store/useHeartRateStore';
import type { ExposureEvent } from '@types';
import { getFoodNameById } from '../../../../data/foods';
import {
  getExposureSpawnPositions,
  getScenarioDisplayLabel,
} from '../../../../data/maps';
import { getExposureConfigFromSession } from '../../../../utils/extractSessionObject';
import { formatTimestampToTime } from '../../../../utils/formatDuration';
import {
  findPortataNumber,
  formatExposureItemEventDetail,
  getExposureItemEventTypeLabel,
  getPlannedExposureGroups,
  getPortateFromExposureConfig,
  type ExposureFoodDisplayContext,
} from '../../../../utils/exposureMultiItem';
import { getExposureEventItemIds } from '../../../../utils/normalizeExposureEvent';

interface ExposureSessionPanelProps {
  session: DeviceConfigurationApi;
}

function getExposureEventLabel(
  event: ExposureEvent,
  isMultiItem: boolean
): string {
  switch (event.type) {
    case 'tutorialCompleted':
      return 'Tutorial completato';
    case 'itemShown':
      return getExposureItemEventTypeLabel('itemShown', isMultiItem);
    case 'itemFeedback':
      return getExposureItemEventTypeLabel('itemFeedback', isMultiItem);
    case 'sessionStartedAt':
      return 'Inizio sessione';
    case 'sessionEndedAt':
      return 'Fine sessione';
    case 'mealEnded':
      return 'Pasto terminato';
    default:
      return String((event as { type: string }).type);
  }
}

function getExposureEventDetail(
  event: ExposureEvent,
  display: ExposureFoodDisplayContext
): string {
  switch (event.type) {
    case 'itemShown':
    case 'itemFeedback': {
      const itemIds = getExposureEventItemIds(event);
      const portataNumber = display.isMultiItem
        ? findPortataNumber(display.portate, itemIds)
        : null;
      return formatExposureItemEventDetail({
        eventType: event.type,
        itemIds,
        portataNumber,
        isMultiItem: display.isMultiItem,
        score: event.type === 'itemFeedback' ? event.score : undefined,
      });
    }
    case 'mealEnded':
      return event.clientId ?? '—';
    default:
      return '—';
  }
}

export const ExposureSessionPanel = React.memo<ExposureSessionPanelProps>(
  ({ session }) => {
    const exposureEvents = useHeartRateStore(state => state.exposureEvents);

    const exposureConfig = useMemo(
      () => getExposureConfigFromSession(session),
      [session]
    );

    const display = useMemo((): ExposureFoodDisplayContext => {
      return {
        isMultiItem: Boolean(exposureConfig.isMultiItem),
        portate: getPortateFromExposureConfig(exposureConfig),
      };
    }, [exposureConfig]);

    const plannedGroups = useMemo(
      () => getPlannedExposureGroups(exposureConfig),
      [exposureConfig]
    );

    const mapLabel = getScenarioDisplayLabel(
      session.type,
      session.scenario,
      session.video?.name
    );

    const spawnLabel = useMemo(() => {
      const spawnId = exposureConfig.userSpawnPosition ?? '1';
      const options = getExposureSpawnPositions(session.scenario);
      return (
        options.find(option => option.value === spawnId)?.label ??
        `Posizione ${spawnId}`
      );
    }, [exposureConfig.userSpawnPosition, session.scenario]);

    const sortedEvents = useMemo(
      () => [...exposureEvents].sort((a, b) => a.timestamp - b.timestamp),
      [exposureEvents]
    );

    const totalPlannedItems = plannedGroups.reduce(
      (sum, group) => sum + group.itemIds.length,
      0
    );

    return (
      <Stack gap='md' p='md' style={{ overflow: 'auto', height: '100%' }}>
        <Card withBorder shadow='sm' radius='sm' p='md'>
          <Stack gap='xs' mb='sm'>
            <Title order={5}>Piano sessione</Title>
            <Text size='xs' c='dimmed'>
              Mappa, spawn e impostazioni inviate al device all&apos;avvio
            </Text>
          </Stack>
          <Grid gutter='md'>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                Mappa
              </Text>
              <Text size='sm' fw={500}>
                {mapLabel}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                Spawn utente
              </Text>
              <Text size='sm' fw={500}>
                {spawnLabel}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                Modalità
              </Text>
              <Text size='sm' fw={500}>
                {display.isMultiItem ? 'Multipla (gruppi)' : 'Singola'}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                Tutorial
              </Text>
              <Text size='sm' fw={500}>
                {exposureConfig.hasTutorial ? 'Sì' : 'No'}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>

        <Card withBorder shadow='sm' radius='sm' p='md'>
          <Stack gap='xs' mb='sm'>
            <Group justify='space-between' align='center'>
              <Title order={5}>Elementi previsti</Title>
              <Badge variant='light' size='sm'>
                {totalPlannedItems} elementi
              </Badge>
            </Group>
            <Text size='xs' c='dimmed'>
              Lista configurata per questa sessione (come nel form di creazione)
            </Text>
          </Stack>

          {plannedGroups.length === 0 ? (
            <Text size='sm' c='dimmed' ta='center' py='md'>
              Nessun elemento configurato
            </Text>
          ) : (
            <Stack gap='md'>
              {plannedGroups.map(group => (
                <Stack key={group.label} gap='xs'>
                  {display.isMultiItem && (
                    <Text size='sm' fw={600}>
                      {group.label}
                    </Text>
                  )}
                  <Table withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={80}>ID</Table.Th>
                        <Table.Th>Nome</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {group.itemIds.map(itemId => (
                        <Table.Tr key={`${group.label}-${itemId}`}>
                          <Table.Td>
                            <Text size='xs' c='dimmed'>
                              {itemId}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size='sm'>{getFoodNameById(itemId)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>

        <Card withBorder shadow='sm' radius='sm' p='md'>
          <Stack gap='xs' mb='sm'>
            <Group justify='space-between' align='center'>
              <Title order={5}>Eventi in sessione</Title>
              <Badge variant='light' size='sm'>
                {sortedEvents.length}
              </Badge>
            </Group>
            <Text size='xs' c='dimmed'>
              Tutorial, elementi mostrati e feedback registrati in tempo reale
            </Text>
          </Stack>

          {sortedEvents.length === 0 ? (
            <Text size='sm' c='dimmed' ta='center' py='md'>
              Nessun evento ancora — compariranno durante la sessione
            </Text>
          ) : (
            <div style={{ maxHeight: '360px', overflow: 'auto' }}>
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Orario</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>Dettaglio</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedEvents.map((event, index) => (
                    <Table.Tr key={`${event.type}-${event.timestamp}-${index}`}>
                      <Table.Td>
                        <Text size='xs'>
                          {formatTimestampToTime(event.timestamp)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant='light' size='xs'>
                          {getExposureEventLabel(event, display.isMultiItem)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='xs' c='dimmed'>
                          {getExposureEventDetail(event, display)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Card>

        <Card withBorder p='xs' bg='blue.0'>
          <Group gap='xs' align='flex-start'>
            <Info size={16} color='var(--mantine-color-blue-filled)' />
            <Text size='xs' c='blue'>
              I gruppi in modalità multipla corrispondono agli indici inviati
              dal device in <code>itemShown</code> / <code>itemFeedback</code>.
            </Text>
          </Group>
        </Card>
      </Stack>
    );
  }
);

ExposureSessionPanel.displayName = 'ExposureSessionPanel';
